import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TopicService, Topic, TopicMessage, TopicMessageRequestDTO, MessageRole, TopicAttachment, Project, TopicStatus, MessageStatus, ProjectCommunicationStatus, Customer } from '../services/topic.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { QuillEditorComponent } from 'ngx-quill';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import Quill from 'quill';
import { Router } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { EndpointFactory } from 'src/app/shared/services/endpoint.service';

@Component({
  selector: 'app-project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss']
})
export class ProjectListComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('editor') editor!: QuillEditorComponent;
  @ViewChild('messageEditor') messageEditor!: QuillEditorComponent;
  @ViewChild('titleInput') titleInput!: ElementRef;

  messageForm: FormGroup;
  newTopicTitle: string = '';
  topics: Topic[] = [];
  selectedTopic?: Topic;
  messages: TopicMessage[] = [];
  currentUserName: string = '';
  submitted = false;
  success = false;
  creatingTopic = false;
  attachments: TopicAttachment[] = [];
  selectedFiles: File[] = [];
  showNotification = false;
  notificationMessage = '';
  unreadMessages: { [topicId: string]: number } = {};

  // Project selection properties
  projects: Project[] = [];
  selectedProject?: Project;
  showProjects = true;

  // Filter properties
  searchText: string = '';
  selectedStatus: string = 'all';
  sortOrder: string = 'newest';
  filteredTopics: Topic[] = [];
  showSidebar = false;

  // Admin customer selection properties
  isAdmin = false;
  showCustomerDropdown = false;
  customerSearchText = '';
  customers: Customer[] = [];
  filteredCustomers: Customer[] = [];
  selectedCustomer: Customer | null = null;
  isCustomerSpecificMessage = false;

  // Message editing properties
  isEditingMessage: string | null = null;
  editingMessageContent: string = '';
  isSelectingCustomer: string | null = null;
  editingAttachments: TopicAttachment[] = [];
  editingImages: string[] = [];
  isEditingTitle = false;
  editingTitle = '';
  hasCyrillicError = false;
  cyrillicErrorMessage = 'Cyrillic characters are not allowed';
  hasTitleCyrillicError = false;
  hasMessageCyrillicError = false;
  hasNewTopicCyrillicError = false;
  isDragging = false;
  loggedInUserId: string = '';
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
    imageHandler: {
      upload: (file: File) => {
        return new Promise((resolve, reject) => {
          // Create a local URL for the image instead of uploading
          const reader = new FileReader();
          reader.onload = (e) => {
            resolve(e.target?.result);
          };
          reader.onerror = (error) => {
            reject(error);
          };
          reader.readAsDataURL(file);
        });
      }
    }
  };

  isEditingStatus = false;

  MessageRole = MessageRole; // Expose MessageRole to template

  unreadMessagesCountByTopic: { [topicId: string]: number } = {};

  getUnreadMessages = false;
  private messageCheckInterval: any;
  private initialCheckTimeout: any;

  @Output() projectSelected = new EventEmitter<Project>();
  @Output() projectArchived = new EventEmitter<Project>();

  hasNoProjects = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private topicService: TopicService,
    private authService: AuthService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private endpointFactory: EndpointFactory
  ) {
    this.messageForm = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(1)]]
    });

    // Configure Quill image format
    const Image = Quill.import('formats/image');
    Image.sanitize = (url: string) => url;
    Image.className = 'editor-image';
    Quill.register(Image, true);
  }

  ngOnInit() {
    this.currentUserName = this.authService.getUserName();
    this.loadProjects();
    this.checkAdminStatus();
    this.loadCustomers();
    this.currentUserName = this.authService.getUserName();

    // Get current user ID
    this.authService.getCurrentUser().subscribe(user => {
      this.loggedInUserId = user.id;
    });

    // Only check for new messages if the user is logged in
    if (this.authService.isLoggedIn()) {
      // timeout on 5 seconds before the first request
      this.initialCheckTimeout = setTimeout(() => {
        this.checkAndUpdateNewMessages();

        // If the user is on the messages page — start the interval
        if (this.router.url.includes('/user/messages')) {
          this.messageCheckInterval = setInterval(() => {
            this.checkAndUpdateNewMessages();
          }, 30000); // 30 seconds
        }
      }, 5000); // 5 seconds
    }
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  shouldScrollToBottom = false;

  private scrollToBottom(): void {
    if (!this.shouldScrollToBottom) return;

    try {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
      this.shouldScrollToBottom = false;
    } catch (err) {}
  }

  loadProjects() {
    this.topicService.getAllProjects().subscribe({
      next: (data) => {
        // Filter out archived projects
        this.projects = data.filter(project => project.status !== ProjectCommunicationStatus.ARCHIVED);
        this.hasNoProjects = this.projects.length === 0;

        // Load topics for each project and update their statuses
        this.projects.forEach(project => {
          this.loadProjectTopics(project);
        });
      },
      error: (error) => {
        if (error.status === 404) {
          this.hasNoProjects = true;
          this.projects = [];
        } else {
          console.error('Failed to load projects:', error);
        }
      }
    });
  }

  private loadProjectTopics(project: Project) {
    this.topicService.getTopicsByProjectId(project.id).subscribe({
      next: (topics) => {
        // Update project status based on its topics
        this.updateProjectStatusBasedOnTopics(project, topics);
      },
      error: (error) => {
        console.error(`Failed to load topics for project ${project.id}:`, error);
      }
    });
  }

  private updateProjectStatusBasedOnTopics(project: Project, topics: Topic[]) {
    console.log(`Checking status for project ${project.id}:`, project.title);
    console.log('Number of topics:', topics.length);
    console.log('Topics:', topics.map(t => ({ id: t.id, title: t.title, status: t.status })));

    // If there are no topics, set status to NEW
    if (!topics.length) {
      console.log('No topics found, setting status to NEW');
      this.updateProjectStatus(project, ProjectCommunicationStatus.NEW);
      return;
    }

    // Check if all topics are resolved
    const allTopicsResolved = topics.every(topic => topic.status === TopicStatus.RESOLVED);
    console.log('All topics resolved?', allTopicsResolved);

    // If all topics are resolved, set project to COMPLETED
    if (allTopicsResolved) {
      console.log('Setting project status to COMPLETED');
      this.updateProjectStatus(project, ProjectCommunicationStatus.COMPLETED);
    }
    // If there are topics but not all are resolved, set to IN_PROGRESS
    else {
      console.log('Setting project status to IN_PROGRESS');
      this.updateProjectStatus(project, ProjectCommunicationStatus.IN_PROGRESS);
    }
  }

  private updateProjectStatus(project: Project, status: ProjectCommunicationStatus): void {
    // Only update if the status is different
    if (project.status === status) {
      console.log('Status unchanged, skipping update');
      return;
    }

    console.log('Updating project status from', project.status, 'to', status);

    const url = this.endpointFactory.UpdateProjectStatus.replace(':id', project.id);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Send status as JSON string
    const body = JSON.stringify(status);

    this.http.patch<any>(url, body, { headers }).subscribe({
      next: (response) => {
        console.log('Status update response:', response);
        if (response.isSuccess) {
          // Update the project status in the projects array
          const projectIndex = this.projects.findIndex(p => p.id === project.id);
          if (projectIndex !== -1) {
            this.projects[projectIndex].status = status;
          }
          // If this is the selected project, update its status too
          if (this.selectedProject?.id === project.id) {
            this.selectedProject.status = status;
          }
          console.log('Project status updated successfully to:', status);
        }
      },
      error: (error) => {
        console.error('Failed to update project status:', error);
      }
    });
  }

  selectProject(project: Project) {
    console.log('Project selected:', project);
    this.projectSelected.emit(project);
  }

  backToProjects() {
    this.showProjects = true;
    this.selectedProject = undefined;
    this.selectedTopic = undefined;
    this.messages = [];
  }

  loadTopics() {
    if (!this.selectedProject) return;

    console.log('Loading topics for project:', this.selectedProject.id);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    this.topicService.getTopicsByProjectId(this.selectedProject.id).subscribe({
      next: (data) => {
        console.log('Received topics:', data);
        // Handle both empty array and 204 No Content response
        this.topics = Array.isArray(data) ? data : [];
        this.applyFilters(); // Apply filters when topics are loaded
        console.log('After applying filters, filteredTopics:', this.filteredTopics);
        // Auto-select the first topic if none is selected
        if (!this.selectedTopic && this.topics.length) {
          this.selectTopic(this.topics[0]);
        }
        // Check and update project status after loading topics
        this.checkAndUpdateProjectStatus();
      },
      error: (error) => {
        console.error('Failed to load topics:', error);
        this.topics = [];
        this.filteredTopics = [];
      }
    });
  }

  selectTopic(topic: Topic) {
    this.selectedTopic = topic;
    this.messages = Array.isArray(topic.messages) ? topic.messages : [];
    this.shouldScrollToBottom = true;
    this.success = false;

    // Clear unread count after 5 seconds
    setTimeout(() => {
      this.unreadMessagesCountByTopic[topic.id] = 0;
      this.clearUnreadCount(topic.id);
    }, 5000);

    // Mark messages as visited when selecting a topic
    if (this.selectedProject) {
      this.topicService.markTopicMessagesAsVisited(this.selectedProject.id, topic.id).subscribe({
        next: () => {
          // Update the unread messages count after marking as visited
          this.checkAndUpdateNewMessages();
        }
      });
    }
  }

  loadMessages(topicId: string) {
    if (!this.selectedProject) return;

    this.topicService.getTopicMessages(this.selectedProject.id, topicId).subscribe({
      next: (topic) => {
        // Update the topic in the topics array
        const index = this.topics.findIndex(t => t.id === topicId);
        if (index !== -1) {
          this.topics[index] = topic;
        }

        // Update selected topic and messages
        if (this.selectedTopic?.id === topicId) {
          this.selectedTopic = topic;
          this.messages = Array.isArray(topic.messages) ? topic.messages : [];
        }

        setTimeout(() => this.scrollToBottom(), 0);
      },
      error: (error) => {
        console.error('Failed to load messages:', error);
      }
    });
  }

  createNewTopic() {
    if (!this.newTopicTitle.trim()) return;

    // Check for Cyrillic characters
    if (this.containsCyrillic(this.newTopicTitle)) {
      this.hasNewTopicCyrillicError = true;
      return;
    }

    // Get current user's email from auth service
    this.authService.getCurrentUser().subscribe(user => {
      if (!user.email) {
        console.error('User email is not available');
        return;
      }

      const members = [user.email]; // Add more members as needed

      if (!this.selectedProject) {
        console.error('No project selected');
        return;
      }

      this.topicService.createTopic(this.newTopicTitle.trim(), members, this.selectedProject.id).subscribe(newTopic => {
        this.loadTopics();
        this.selectTopic(newTopic);
        this.newTopicTitle = '';
        this.creatingTopic = false;
        this.hasNewTopicCyrillicError = false;
      });
    });
  }

  checkNewTopicCyrillic() {
    this.hasNewTopicCyrillicError = this.containsCyrillic(this.newTopicTitle);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.selectedTopic) {
      return;
    }

    Array.from(input.files).forEach(file => {
      // Show loading state
      this.showNotification = true;
      this.notificationMessage = `Uploading ${file.name}...`;

      this.topicService.uploadAttachment(file, this.selectedTopic!.id).subscribe({
        next: (attachment) => {
          if (this.isEditingMessage) {
            this.editingAttachments.push(attachment);
          } else {
            this.attachments.push(attachment);
            this.selectedFiles.push(file);
          }
          // Show success message
          this.showNotification = true;
          this.notificationMessage = `Successfully uploaded ${file.name}`;
        },
        error: (error) => {
          console.error('Failed to upload file:', error);
          // Show error notification
          this.showNotification = true;
          this.notificationMessage = `Failed to upload ${file.name}: ${error.message}`;
        }
      });
    });

    // Clear the input
    input.value = '';
  }

  removeAttachment(attachment: TopicAttachment) {
    this.attachments = this.attachments.filter(a => a.url !== attachment.url);
    this.selectedFiles = this.selectedFiles.filter(f => f.name !== attachment.fileName);
    this.topicService.deleteFileByUrl(attachment.url).subscribe({
      next: () => {
        console.log('File deleted successfully');
      },
      error: (err) => {
        console.error('Failed to delete file:', err);
      }
    });
  }

  downloadAttachment(attachment: TopicAttachment) {
    this.topicService.downloadAttachment(attachment);
  }

  onSubmit() {
    this.submitted = true;
    this.success = false;

    if (this.messageForm.invalid || !this.selectedTopic) return;

    const messageContent = this.editor.quillEditor.root.innerHTML;

    if (!messageContent.trim() && this.attachments.length === 0) return;

    // Check for Cyrillic characters in the message content
    if (this.containsCyrillic(messageContent)) {
      this.hasMessageCyrillicError = true;
      return;
    }

    this.authService.getCurrentUser().subscribe(user => {
      if (!user.id || !this.selectedTopic) {
        console.error('User ID or selected topic is not available');
        return;
      }

      const messageDTO: TopicMessageRequestDTO = {
        topicStatus: this.selectedTopic.status,
        authorId: user.id,
        authorName: this.currentUserName,
        allowedRoles: [MessageRole.DRAFTER, MessageRole.ADMIN, MessageRole.CUSTOMER_ADMIN],
        members: this.isCustomerSpecificMessage && this.selectedCustomer
          ? [this.selectedCustomer.email]
          : this.selectedTopic.members,
        content: messageContent,
        imageUrls: [],
        attachments: this.attachments,
        role: MessageRole.DRAFTER,
        id: this.selectedTopic.id,
        projectId: this.selectedProject!.id
      };

      this.topicService.sendMessage(messageDTO).subscribe(() => {
        this.success = true;
        this.messageForm.controls['message'].setValue('');
        this.attachments = [];
        this.selectedFiles = [];
        this.isCustomerSpecificMessage = false;

        const Delta = Quill.import('delta');
        this.editor.quillEditor.setContents(new Delta());

        this.submitted = false;
        this.shouldScrollToBottom = true;
        this.loadMessages(this.selectedTopic!.id);
      });
    });
  }

  sanitizeHtml(html: string): SafeHtml {
    // Create a temporary div to manipulate the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all images and add custom styles
    const images = tempDiv.getElementsByTagName('img');
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      img.style.maxWidth = '200px';
      img.style.maxHeight = '150px';
      img.style.objectFit = 'contain';
      img.style.borderRadius = '4px';
      img.style.margin = '8px 0';
      img.style.cursor = 'pointer';
      img.style.transition = 'transform 0.2s ease';
      img.classList.add('message-image');
    }

    // Remove margins from all other elements and add list indentation
    const elements = tempDiv.getElementsByTagName('*');
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      if (element.tagName !== 'IMG') {  // Skip images as they're handled above
        element.style.margin = '0';
        element.style.padding = '0';

        // Add left padding for lists
        if (element.tagName === 'UL' || element.tagName === 'OL') {
          element.style.paddingLeft = '20px';
        }
      }
    }

    return this.sanitizer.bypassSecurityTrustHtml(tempDiv.innerHTML);
  }

  get f() {
    return this.messageForm.controls;
  }

  getFileTypeIcon(fileType: string): string {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) {
      return 'pdf';
    } else if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xls') || type.includes('xlsx')) {
      return 'excel';
    } else if (type.includes('word') || type.includes('doc') || type.includes('docx')) {
      return 'word';
    } else if (type.includes('image')) {
      return 'image';
    } else if (type.includes('zip') || type.includes('rar') || type.includes('7z')) {
      return 'archive';
    } else {
      return 'file';
    }
  }

  getFileTypeLabel(fileType: string): string {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) {
      return 'PDF Document';
    } else if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xls') || type.includes('xlsx')) {
      return 'Excel Spreadsheet';
    } else if (type.includes('word') || type.includes('doc') || type.includes('docx')) {
      return 'Word Document';
    } else if (type.includes('image')) {
      return 'Image';
    } else if (type.includes('zip') || type.includes('rar') || type.includes('7z')) {
      return 'Archive';
    } else {
      return 'File';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  incrementUnreadCount(topicId: string) {
    this.unreadMessages[topicId] = (this.unreadMessages[topicId] || 0) + 1;
  }

  clearUnreadCount(topicId: string) {
    this.unreadMessages[topicId] = 0;
  }

  handleNotificationAction(action: 'view' | 'dismiss') {
    if (action === 'view') {
      const topic = this.topics.find(t => t.id === this.selectedTopic?.id);
      if (topic) {
        this.selectTopic(topic);
        this.clearUnreadCount(topic.id);
      }
    }
    this.showNotification = false;
  }

  // Filter methods
  onSearchInput(value: string) {
    this.searchText = value;
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.topics];

    // Apply search filter
    if (this.searchText) {
      const searchTerm = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(topic =>
        topic.title.toLowerCase().includes(searchTerm)
      );
    }

    // Apply status filter
    if (this.selectedStatus !== 'all') {
      filtered = filtered.filter(topic => topic.status === this.selectedStatus);
    }

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.created).getTime();
      const dateB = new Date(b.created).getTime();
      return this.sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    this.filteredTopics = filtered;
  }

  clearFilters() {
    this.searchText = '';
    this.selectedStatus = 'all';
    this.sortOrder = 'newest';
    this.applyFilters();
  }

  // Add method to check if any filters are active
  hasActiveFilters(): boolean {
    return this.searchText !== '' ||
           this.selectedStatus !== 'all' ||
           this.sortOrder !== 'newest';
  }

  toggleSidebar() {
    this.showSidebar = !this.showSidebar;
  }

  isFilterActive(): boolean {
    return this.searchText !== '' ||
           this.selectedStatus !== 'all' ||
           this.sortOrder !== 'newest';
  }

  private checkAdminStatus() {
    this.isAdmin = this.authService.isAdmin();
  }

  private loadCustomers() {
    if (!this.selectedProject) return;

    // Get all unique client IDs from project members
    const clientIds = [...new Set(this.selectedProject.members)];

    // Fetch customers for each client ID
    const customerPromises = clientIds.map(clientId =>
      this.topicService.getCustomersByClientId(clientId).toPromise()
    );

    Promise.all(customerPromises)
      .then(results => {
        // Flatten and deduplicate customers, filtering out undefined values
        const allCustomers = results
          .filter((result): result is Customer[] => Array.isArray(result))
          .flat()
          .filter((customer): customer is Customer =>
            customer !== undefined &&
            customer !== null &&
            typeof customer.id === 'string'
          );

        // Remove duplicates based on id
        this.customers = allCustomers.filter((customer, index, self) =>
          index === self.findIndex(c => c.id === customer.id)
        );

        this.filteredCustomers = [...this.customers];
        console.log('Loaded customers:', this.customers); // Debug log
      })
      .catch(error => {
        console.error('Failed to load customers:', error);
        this.customers = [];
        this.filteredCustomers = [];
      });
  }

  getCustomerName(email: string): string {
    const customer = this.customers.find(c => c.email === email);
    return customer ? customer.name : email;
  }

  toggleStatusEdit() {
    this.isEditingStatus = !this.isEditingStatus;
  }

  onStatusChange() {
    if (!this.selectedTopic || !this.selectedProject) return;

    this.topicService.updateTopicStatus(this.selectedProject.id, this.selectedTopic.id, this.selectedTopic.status)
      .subscribe({
        next: () => {
          this.isEditingStatus = false;
          // Check and update project status after topic status changes
          this.checkAndUpdateProjectStatus();
        },
        error: (error) => {
          console.error('Failed to update status:', error);
          // Revert the status change if the API call fails
          this.selectedTopic!.status = this.selectedTopic!.status;
        }
      });
  }

  toggleMessageEdit(message: TopicMessage) {
    if (this.isEditingMessage === message.id) {
      this.cancelMessageEdit();
    } else {
      this.isEditingMessage = message.id;
      this.editingMessageContent = message.content;
      this.editingAttachments = [...(message.attachments || [])];
      this.editingImages = [...(message.imageUrls || [])];
      this.isSelectingCustomer = null;
      this.hasMessageCyrillicError = false;
    }
  }

  cancelMessageEdit() {
    this.isEditingMessage = null;
    this.editingMessageContent = '';
    this.editingAttachments = [];
    this.editingImages = [];
    this.hasMessageCyrillicError = false;
  }

  saveMessageEdit(message: TopicMessage) {
    if (this.containsCyrillic(this.editingMessageContent)) {
      this.hasMessageCyrillicError = true;
      return;
    }

    if (!this.selectedTopic || !this.selectedProject) {
      console.error('No topic or project selected');
      return;
    }

    const updatedMessage: TopicMessage = {
      ...message,
      content: this.editingMessageContent,
      attachments: this.editingAttachments,
      imageUrls: this.editingImages,
      discussionId: this.selectedTopic.id
    };

    this.topicService.updateMessage(updatedMessage, this.selectedProject.id).subscribe(
      () => {
        this.isEditingMessage = null;
        this.editingMessageContent = '';
        this.editingAttachments = [];
        this.editingImages = [];
        this.hasMessageCyrillicError = false;
        this.loadMessages(this.selectedTopic!.id);
      },
      error => {
        console.error('Error updating message:', error);
      }
    );
  }

  deleteEditingAttachment(attachment: TopicAttachment) {
    this.topicService.deleteFileByUrl(attachment.url).subscribe({
      next: () => {
        this.editingAttachments = this.editingAttachments.filter(a => a.url !== attachment.url);
      },
      error: (err) => {
        console.error('Failed to delete file:', err);
      }
    });
  }

  deleteEditingImage(imageUrl: string) {
    this.editingImages = this.editingImages.filter(url => url !== imageUrl);
  }

  toggleCustomerSelection(message: TopicMessage) {
    if (this.isSelectingCustomer === message.id) {
      this.isSelectingCustomer = null;
    } else {
      this.isSelectingCustomer = message.id;
      this.isEditingMessage = null;
      this.loadCustomers();
    }
  }

  selectCustomerForTopic(customer: Customer) {
    if (!this.selectedTopic || !this.selectedProject) {
      console.error('No topic or project selected');
      return;
    }

    // Get the message being edited
    const message = this.messages.find(m => m.id === this.isSelectingCustomer);
    if (!message) {
      console.error('No message found for customer selection');
      return;
    }

    // Update the message's allowedRoles and members
    const updatedMessage: TopicMessage = {
      ...message,
      allowedRoles: [...(message.allowedRoles || []), MessageRole.CUSTOMER_ADMIN],
      members: message.members?.includes(customer.email)
        ? message.members.filter(email => email !== customer.email)
        : [...(message.members || []), customer.email],
      discussionId: this.selectedTopic.id
    };

    // Update the message through the service
    this.topicService.updateMessage(updatedMessage, this.selectedProject.id).subscribe(
      () => {
        // Update the local message
        const index = this.messages.findIndex(m => m.id === message.id);
        if (index !== -1) {
          this.messages[index] = updatedMessage;
        }
        this.isSelectingCustomer = null;
      },
      error => {
        console.error('Failed to update message:', error);
      }
    );
  }

  archiveProject(project: Project, event: Event) {
    // Prevent the card click event from firing
    event.stopPropagation();

    // Show confirmation dialog
    if (confirm(`Are you sure you want to archive the project "${project.title}"?`)) {
      this.topicService.updateProjectStatus(project.id, ProjectCommunicationStatus.ARCHIVED)
        .subscribe({
          next: () => {
            // Remove the project from the list
            this.projects = this.projects.filter(p => p.id !== project.id);
            this.projectArchived.emit(project);
          },
          error: (error) => {
            console.error('Failed to archive project:', error);
          }
        });
    }
  }

  toggleTitleEdit() {
    this.isEditingTitle = !this.isEditingTitle;
    if (this.isEditingTitle && this.selectedTopic) {
      this.editingTitle = this.selectedTopic.title;
      this.hasTitleCyrillicError = false;
      // Focus the input after the view updates
      setTimeout(() => {
        this.titleInput?.nativeElement?.focus();
      });
    }
  }

  saveTitleEdit() {
    if (!this.editingTitle.trim() || !this.selectedTopic || !this.selectedProject) return;

    // Check for Cyrillic characters
    if (this.containsCyrillic(this.editingTitle)) {
      this.hasTitleCyrillicError = true;
      return;
    }

    const projectId = this.selectedProject.id;
    const topicId = this.selectedTopic.id;
    const newTitle = this.editingTitle.trim();

    // Update the topic title
    this.topicService.updateTopicTitle(projectId, topicId, newTitle).subscribe({
      next: () => {
        if (this.selectedTopic) {
          this.selectedTopic.title = newTitle;
        }
        this.isEditingTitle = false;
        this.hasTitleCyrillicError = false;
      },
      error: (error) => {
        console.error('Failed to update topic title:', error);
        // Revert the change if the API call fails
        if (this.selectedTopic) {
          this.editingTitle = this.selectedTopic.title;
        }
      }
    });
  }

  cancelTitleEdit() {
    this.isEditingTitle = false;
    this.editingTitle = '';
  }

  private containsCyrillic(text: string): boolean {
    // Cyrillic Unicode range: U+0400 to U+04FF
    return /[\u0400-\u04FF]/.test(text);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files?.length || !this.selectedTopic) {
      return;
    }

    Array.from(files).forEach(file => {
      // Show loading state
      this.showNotification = true;
      this.notificationMessage = `Uploading ${file.name}...`;

      this.topicService.uploadAttachment(file, this.selectedTopic!.id).subscribe({
        next: (attachment) => {
          if (this.isEditingMessage) {
            this.editingAttachments.push(attachment);
          } else {
            this.attachments.push(attachment);
            this.selectedFiles.push(file);
          }
          // Show success message
          this.showNotification = true;
          this.notificationMessage = `Successfully uploaded ${file.name}`;
        },
        error: (error) => {
          console.error('Failed to upload file:', error);
          // Show error notification
          this.showNotification = true;
          this.notificationMessage = `Failed to upload ${file.name}: ${error.message}`;
        }
      });
    });
  }

  isCustomerSelectedForMessage(customer: Customer, messageId: string): boolean {
    const message = this.messages.find(m => m.id === messageId);
    if (!message) return false;

    return message.members?.includes(customer.email) &&
           message.allowedRoles?.includes(MessageRole.CUSTOMER_ADMIN) || false;
  }

  isMyMessage(msg: TopicMessage): boolean {
    return msg.authorId === this.loggedInUserId;
  }

  isCustomerAdminMessage(msg: any): boolean {
    return msg.allowedRoles?.includes('CustomerAdmin');
  }

  ngOnDestroy() {
    if (this.messageCheckInterval) {
      clearInterval(this.messageCheckInterval);
    }
    if (this.initialCheckTimeout) {
      clearTimeout(this.initialCheckTimeout);
    }
  }

  private checkAndUpdateNewMessages(): void {
    this.topicService.checkNewMessages().subscribe({
      next: (result) => {
        this.unreadMessagesCountByTopic = {};

        Object.entries(result).forEach(([topicId, messages]) => {
          const unreadCount = messages.filter(msg => !msg.readBy || msg.readBy.length === 0).length;
          if (unreadCount > 0) {
            this.unreadMessagesCountByTopic[topicId] = unreadCount;
          }
        });

        this.getUnreadMessages = Object.keys(this.unreadMessagesCountByTopic).length > 0;
        console.log('Unread messages by topic:', this.unreadMessagesCountByTopic);
        console.log('Has unread messages:', this.getUnreadMessages);
      },
      error: (err) => {
        console.error('Error fetching new messages:', err);
        this.getUnreadMessages = false;
        this.unreadMessagesCountByTopic = {};
      }
    });
  }

  getTotalUnreadMessages(): number {
    return Object.values(this.unreadMessagesCountByTopic).reduce((sum, count) => sum + count, 0);
  }

  private checkAndUpdateProjectStatus(): void {
    if (!this.selectedProject) return;
    this.loadProjectTopics(this.selectedProject);
  }

  // Add these event handler methods for the template
  onProjectSelected(project: Project) {
    this.selectedProject = project;
    this.showProjects = false;
    // Optionally, you can load topics here or let the sidebar handle it
  }

  onProjectArchived(project: Project) {
    // Optionally handle project archived event
    if (this.selectedProject && this.selectedProject.id === project.id) {
      this.backToProjects();
    }
  }

  onTopicSelected(topic: Topic) {
    this.selectedTopic = topic;
    this.messages = Array.isArray(topic.messages) ? topic.messages : [];
    this.shouldScrollToBottom = true;
    this.success = false;
    // Optionally, mark messages as visited, etc.
  }
}
